import { Subscription } from 'rxjs';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { MsgStatus } from 'src/lib/fliko/models/integrations/sendDataBase.data';
import { EmailQueueData, EmailTemplateType } from 'src/lib/fliko/models/messageQueue/email.data';
import { NewsData, NewsNotificationType, NewsReceiversData, NewsStatus, NewsType } from 'src/lib/fliko/models/news/news.data';
import { ResidentType, ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { EstateService } from 'src/app/view/estate-view/services/estate.service';
import { NewsHelper } from 'src/app/view/news/helpers/news.helper';
import { NewsService } from 'src/app/view/news/service/news.service';
import { NewsValidators } from 'src/app/view/news/validators/newsValidators';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { EMAIL_ATTACHMENT_SIZE_LIMIT } from '../../constants/email.constants';
import { ErrorHandlingService } from '../../error-handling/error-handling.service';
import { FileInStorageVM } from '../../models/file.data';
import { NotificationContentVM } from '../../models/notification-view-model';
import { NotificationService } from '../../services/notification.service';
import { PushIdService } from '../../services/pushId.service';
import { SendEmailService } from '../../services/send-email.service';
import { AddAttachmentFieldComponent } from '../add-attachment-field/add-attachment-field.component';
import { AddPictureFieldComponent } from '../add-picture-field/add-picture-field.component';
import { NotificationTypeSelectComponent } from '../notification-type-select/notification-type-select.component';
import { RichTextEditorComponent } from '../rich-text-editor/rich-text-editor.component';

export interface ExternNewsFormValue {
  title: string;
  description: string;
  dateFrom: Date;
  dateTo: Date;
  receivers?: NewsReceiversData;
  notificationRecipients: ResidentType[] | null;
  attachments: FileInStorageVM[];
  photos: FileInStorageVM[];
}

export class ExternFormError extends Error {
  constructor(msg: string) {
    super(msg);
  }
}

@Component({
  selector: 'app-extern-news-form[setup][target]',
  templateUrl: './extern-news-form.component.html',
  styleUrls: ['./extern-news-form.component.scss']
})
export class ExternNewsFormComponent implements OnInit {
  @Input() setup!: ExternNewsFormValue;
  @Input() target!: ResidentData[];
  @Input() ntfTemplate?: NotificationContentVM;

  public readonly FILE_SIZE_LIMIT = EMAIL_ATTACHMENT_SIZE_LIMIT;
  public readonly NOW = new Date();

  public edit = false;
  public form = this.fb.group(
    {
      title: [null, Validators.required],
      dateFrom: [null, NewsValidators.date],
      dateTo: [null, NewsValidators.date]
    },
    { validators: NewsValidators.datesRange }
  );

  public emails!: Set<string>;
  public users!: Set<string>;

  public get loaded(): boolean { return !!this.estate && !!this.emails; } // prettier-ignore

  public notificationTypes = NewsHelper.getDefaultNotificationTypes();
  public emailFlag: boolean = false;
  public fileSize: { photos: number; attachments: number } = { photos: 0, attachments: 0 };

  public get attachmentSizeSum(): number { return this.fileSize.photos + this.fileSize.attachments; } // prettier-ignore
  public get emailNotificationsSelected(): boolean {
    return this.emailFlag && !!this.emails?.size && this.notificationTypes.has(NewsNotificationType.EMAIL);
  }
  public get attachmentsOverLimit(): boolean {
    return this.emailNotificationsSelected && this.attachmentSizeSum > this.FILE_SIZE_LIMIT;
  }

  private estate!: EstateData;

  private subscription$ = new Subscription();

  @ViewChild(NotificationTypeSelectComponent) notificationSelect!: NotificationTypeSelectComponent;
  @ViewChild(AddPictureFieldComponent) pictureFieldComponent!: AddPictureFieldComponent;
  @ViewChild(AddAttachmentFieldComponent) attachmentFieldComponent!: AddAttachmentFieldComponent;
  @ViewChild(RichTextEditorComponent) richTextEditorComponent!: RichTextEditorComponent;

  constructor(
    private fb: FormBuilder,
    private estateService: EstateService,
    private newsService: NewsService,
    private sendEmailService: SendEmailService,
    private notificationService: NotificationService,
    private ctxDS: ContextDataService,
    private errorHandlingService: ErrorHandlingService,
    private pushId: PushIdService
  ) {}

  ngOnInit(): void {
    this.subscription$.add(this.ctxDS.currentEstate$.subscribe((x) => (this.estate = x!)));
    this.subscription$.add(this.estateService.getFeatures$().subscribe(
      (features) => (this.emailFlag = features?.web.mailing.newsTemplate ?? false))
    ); // prettier-ignore

    this.form.patchValue({
      title: this.setup.title,
      dateFrom: this.setup.dateFrom,
      dateTo: this.setup.dateTo
    });

    this.getData();
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  private getData(): void {
    this.emails = new Set();
    this.users = new Set();
    this.target.forEach((resident) => {
      if (resident.email) this.emails.add(ToString.getEmail(resident));
      if (resident.userId) this.users.add(resident.userId);
    });

    if (!this.emails.size && !this.users.size) {
      this.errorHandlingService.showGlobalError(this.errorHandlingService.getErrorText('noValidTarget')!);
    } else if (!this.emails.size) {
      this.errorHandlingService.showInvalidFormWarning(this.errorHandlingService.getErrorText('noEmailTarget')!);
    } else if (!this.users.size) {
      this.errorHandlingService.showInvalidFormWarning(this.errorHandlingService.getErrorText('noMobileTarget')!);
    }
  }

  // ! called in parent component!
  public get invalid(): boolean {
    this.form.markAllAsTouched();
    return !this.loaded || this.form.invalid || this.notificationSelect.invalid || this.attachmentsOverLimit;
  }

  // ! called in parent component!
  public async onSubmit() {
    const description = this.richTextEditorComponent.onSubmit();
    if (!this.loaded) throw new ExternFormError('Data not loaded');
    if (this.form.invalid || !description?.length) throw new ExternFormError('News form invalid');
    if (!this.emails.size && !this.users.size) throw new ExternFormError('noValidTarget');
    if (this.notificationSelect.invalid) throw new ExternFormError('Notification type select invalid');
    if (this.attachmentsOverLimit) throw new ExternFormError('Email attachment size limit exceeded');

    const value = this.form.value;
    const now = this.NOW.valueOf();

    const [photos, attachments] = await Promise.all([
      this.pictureFieldComponent.onSubmit(),
      this.attachmentFieldComponent.onSubmit()
    ]);

    const news: NewsData = {
      ...this.setup,
      ...value,
      description,
      dateFrom: value.dateFrom.valueOf(),
      dateTo: value.dateTo.valueOf(),

      id: this.pushId.create(),
      estateId: this.estate.id,

      status: NewsStatus.ACTIVE,
      type: NewsType.INFO,
      publishDate: now,

      photos: photos.map((x) => x.storagePath).join('|'),
      photosUrl: photos.map((x) => x.downloadUrl).join('|'),
      attachments: attachments.map((x) => x.storagePath)
    };

    await this.newsService.setNews(news);

    const promises: Promise<void>[] = [];

    if (this.emailNotificationsSelected) {
      const currentUserEmail = this.ctxDS.peekCurrentUser()?.email ?? '';
      const email: EmailQueueData = {
        id: this.pushId.create(),
        senderEmail: currentUserEmail,
        estateId: this.estate.id,
        templateType: EmailTemplateType.NEWS,
        emailFor: Array.from(this.emails).join(','),
        subject: news.title,
        message: news.description,
        attachmentsUrls: [...photos, ...attachments].map((x) => x.storagePath).join('|'),
        additionalData: { newsId: news.id },
        sendDate: now,
        createDate: now,
        status: MsgStatus.PENDING
      };

      promises.push(this.sendEmailService.sendEmail(email));
    }

    if (this.ntfTemplate && this.notificationTypes.has(NewsNotificationType.MOBILE)) {
      this.ntfTemplate.additionalData.foreignId += '|' + news.id;
      await this.notificationService.sendNotification({
        ...this.ntfTemplate,
        estateId: this.estate.id,
        target: { userIds: [...this.users] }
      });
    }

    if (this.notificationTypes.has(NewsNotificationType.SMS)) {
      // TODO: implement
    }

    await Promise.all(promises);
  }
}
